import { Eye, EyeOff } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useId, type ReactElement } from 'react';

import { Button, IconButton, Notice, Skeleton } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { useSetWatchState, watchStateQueryOptions, type WatchTarget } from '@/lib/watch';

export interface WatchToggleProps {
  target: WatchTarget;
  label: string;
  description: string;
  enabled?: boolean | undefined;
  compact?: boolean | undefined;
  /**
   * The answer the caller **already has** for this viewer, used as the toggle's
   * state until `GET /…/:id/watch` returns — `boardFaceCardSchema.watching`
   * (`FE-29.1`, `FR-COLLAB-022`).
   *
   * -------------------------------------------------------------------------
   * WHY A SEEDED PROP AND NOT A SECOND REQUEST OR A CACHE WRITE-THROUGH
   * -------------------------------------------------------------------------
   * The board face now projects a per-viewer `watching` on every card it
   * serves, in **one** statement for the whole board (`= ANY(faceCardIds)`),
   * which is what `FE-29.1`'s residual demanded *without N+1 per-card reads*.
   * `packages/shared/src/watch.ts` keeps its **card-back** refusal — there is
   * no `watching` on `cardDetailSchema` and `GET /cards/:id/watch` still ships
   * behind `requireUser` — so this component keeps its own request and keeps
   * being the writer. What the projection adds is a first answer that costs
   * nothing: a caller that has already read the board face knows this viewer's
   * exact card-scoped watch state before the second request is even sent.
   *
   * ⚠ **Three states, and the third is not "false".** The field is
   * `z.boolean().nullable()`; `null` means *"there is no you"* (an anonymous
   * caller), and it also means *"no viewer answer is available on this route"*
   * on `cardMirrorSchema`'s two routes, which answer `null` even to a signed-in
   * caller. Both of those are **"seed nothing"** here, which is why `null` and
   * `undefined` behave identically and why the type is not `boolean`. Reading
   * `null` as `false` would assert that somebody who does not exist is not
   * watching, and would light the toggle "Watch" for a mirror whose source the
   * viewer really does watch.
   *
   * ⚠ **It is a seed, never the truth.** The query still runs and still wins
   * the moment it resolves, and the mutation still writes through
   * `useSetWatchState`. A stale board face therefore self-corrects rather than
   * sticking.
   *
   * ⚠ **Omitting the prop reproduces the previous behaviour on every path but
   * ONE, and the exception is recorded here rather than left to be found.**
   * The three branches below are now guarded on `known === undefined` — "have
   * we any answer at all" — instead of on the query alone. For the skeleton
   * that is exactly equivalent with no seed (`isLoading` implies `status ===
   * 'pending'`, which implies `data === undefined`), so it costs nothing. For
   * the error branch it is **not**: TanStack keeps `data` from an earlier
   * success when a later refetch fails, so `isError === true` with
   * `data !== undefined` is reachable, and this component used to replace a
   * working toggle with the "Could not load watch state" notice and its Retry
   * when that happened. It no longer does — it keeps rendering the last known
   * state. That is the same rule the seed follows ("render the best answer we
   * have, whatever its source") applied to the request's own stale answer, it
   * is deliberate, and `WatchToggle.test.tsx` pins it.
   */
  initialWatching?: boolean | null | undefined;
  /**
   * `'described'` keeps the description out of the layout: it becomes the
   * toggle's accessible description (`aria-describedby` on a visually hidden
   * node) instead of a visible sentence under it. The card back passes it — the
   * sentence spent a full line above the description on every card (D1 amended,
   * 2026-10-09). Default `'visible'`.
   */
  descriptionDisplay?: 'visible' | 'described' | undefined;
}

export function WatchToggle({
  target,
  label,
  description,
  enabled = true,
  compact = false,
  initialWatching,
  descriptionDisplay = 'visible',
}: WatchToggleProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const descriptionId = useId();
  const state = useQuery(watchStateQueryOptions(target, enabled));
  const setWatch = useSetWatchState();
  /**
   * The best answer available right now: the request's if it has one, otherwise
   * the board face's projection, otherwise none at all. `??` and not `||` — the
   * meaningful value here is `false`, and only `null`/`undefined` may fall
   * through.
   */
  const known: boolean | undefined = state.data?.watching ?? initialWatching ?? undefined;
  const watching = known === true;
  const busy = (state.isLoading && known === undefined) || setWatch.isPending;

  function toggle() {
    if (!enabled || known === undefined || setWatch.isPending) return;
    setWatch.mutate({
      targetType: target.targetType,
      targetId: target.targetId,
      watching: !known,
    });
  }

  if (!enabled) {
    if (compact) {
      return (
        <IconButton
          label={t('primitives.watchToggle.watch', { label })}
          size="sm"
          variant="quiet"
          disabled
          className="shrink-0"
        >
          <Eye aria-hidden="true" className="size-3.5" />
        </IconButton>
      );
    }

    return (
      <Button
        size="sm"
        variant="quiet"
        disabled
        disabledReason={t('primitives.watchToggle.disabledReason')}
      >
        {t('primitives.watchToggle.watch', { label })}
      </Button>
    );
  }

  if (state.isLoading && known === undefined) {
    return compact ? (
      <Skeleton shape="line" className="h-7 w-7 shrink-0" />
    ) : (
      <div className="flex flex-col gap-2">
        <Skeleton shape="line" className="h-7 w-28" />
        <Skeleton shape="line" className="h-4 w-full" />
      </div>
    );
  }

  if ((state.isError || state.data === undefined) && known === undefined) {
    if (compact) {
      return (
        <IconButton
          label={t('primitives.watchToggle.retryCompact', { label })}
          size="sm"
          variant="quiet"
          className="shrink-0"
          onClick={() => void state.refetch()}
        >
          <Eye aria-hidden="true" className="size-3.5" />
        </IconButton>
      );
    }

    return (
      <Notice
        tone="bad"
        title={t('primitives.watchToggle.loadFailed.title')}
        body={t('primitives.watchToggle.loadFailed.body')}
        action={
          <Button size="sm" onClick={() => void state.refetch()}>
            {t('primitives.watchToggle.retry')}
          </Button>
        }
      />
    );
  }

  const text = watching
    ? t('primitives.watchToggle.unwatch', { label })
    : t('primitives.watchToggle.watch', { label });
  const icon = watching ? (
    <EyeOff aria-hidden="true" className="size-3.5" />
  ) : (
    <Eye aria-hidden="true" className="size-3.5" />
  );

  if (compact) {
    return (
      <IconButton
        label={text}
        size="sm"
        variant="quiet"
        aria-pressed={watching}
        busy={busy}
        className="shrink-0"
        onClick={toggle}
      >
        {icon}
      </IconButton>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Button
        size="sm"
        variant="toggle"
        pressed={watching}
        busy={busy}
        busyLabel={t('primitives.watchToggle.busy')}
        iconStart={icon}
        onClick={toggle}
        className="self-start"
        aria-describedby={descriptionDisplay === 'described' ? descriptionId : undefined}
      >
        {text}
      </Button>
      {descriptionDisplay === 'described' ? (
        <p id={descriptionId} className="sr-only">
          {description}
        </p>
      ) : (
        <p className="text-sm text-ink-muted">{description}</p>
      )}
    </div>
  );
}
